import { useMemo } from 'react'
import { useTranslation } from 'next-i18next'

import { logEvent } from '@/lib/gtm'
import env from '@/lib/env'

import type { ServiceContent } from '@/features/services/components/ServiceCatalog/types'
import type { HeroProps } from '@/features/services/templates/Hero'
import type { ServiceInfoProps } from '@/features/services/templates/ServiceInfo'

import DesktopBgImage from '@/images/services/dpo/banner-desktop.png'
import MobileBgImage from '@/images/services/dpo/banner-mobile.png'
import InfoImage from '@/images/services/dpo/info.png'

const FALLBACK_IMAGE = '/images/placeholder.jpg'

export const useMeta = () => {
  const { t } = useTranslation('services')

  return {
    title: t('dpo.meta.title'),
    description: t('dpo.meta.description'),
    og: {
      title: t('dpo.meta.og.title'),
      description: t('dpo.meta.og.description'),
      image: {
        url: `${env.APP_URL}${t('training.meta.og.image.url')}`,
      },
    },
  }
}

export const useHeroData = (): HeroProps => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => ({
      title: t('dpo.title'),
      subtitle: t('dpo.subtitle'),
      primaryButtonLabel: t('dpo.primaryButtonLabel'),
      primaryButtonLink: { hash: t('dpo.primaryButtonLink') },
      secondaryButtonLabel: t('dpo.secondaryButtonLabel'),
      secondaryButtonLink: t('dpo.secondaryButtonLink'),
      image: {
        base: `url(${MobileBgImage.src})`,
        md: `url(${DesktopBgImage.src})`,
      },
      titleColor: 'primary.400',
      subtitleColor: 'gray.900',
      colorSchemePrimary: 'primary',
      colorSchemeSecondary: 'primary',
      bgColor: 'gray.25',
      onClickGetQuote: (): void => {
        logEvent({
          ga: {
            category: 'DPO',
            action: 'Click get quote'
          },
        })
      },
      onClickGetHandout: (): void => {
        logEvent({
          ga: {
            category: 'DPO',
            action: 'Download handout',
            label: 'header'
          },
        })
      },
    }),
    [language],
  )
}

export const useServiceInfoData = (): ServiceInfoProps => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => ({
      title: t('dpo.serviceInfo.title'),
      paragraph: t('dpo.serviceInfo.paragraph'),
      image: InfoImage,
    }),
    [language],
  )
}

interface RawServiceData {
  id: string
  topic: string
  description: string
  buttonLink: string
  readMoreLabel?: string
  readMoreIcon?: string
  benefitTitle: string
  benefits: string[]
  targetTitle: string
  targetDescrition: string
  price: string
  priceDetail: string
  registerButtonLabel: string
  registerLink: string
  imageUrl?: string
}

export const useServiceCatalogData = (): ServiceContent[] => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  const resolveImageUrl = (url?: string): string => {
    if (!url) return ''
    const cleanPath = url.replace(/^public\/?/, '').replace(/^\//, '')
    if (cleanPath.startsWith('http')) return url
    return `/${cleanPath}`
  }

  return useMemo(() => {
    const rawList = t('dpo.catalog.training', { returnObjects: true }) as any

    if (!Array.isArray(rawList)) return []

    return rawList.map((item: RawServiceData) => ({
      id: item.id || 'unknown-id',
      topic: item.topic || '',
      description: item.description || '',
      buttonLink: item.buttonLink || '',
      readMoreLabel: item.readMoreLabel || '',
      readMoreIcon: resolveImageUrl(item.readMoreIcon),
      benefitTitle: item.benefitTitle || '',
      benefits: item.benefits || [],
      targetTitle: item.targetTitle || '',
      targetDescription: item.targetDescrition || '',
      price: item.price || '',
      priceDetail: item.priceDetail || '',
      registerButtonLabel: item.registerButtonLabel || 'Register',
      registerLink: item.registerLink || '',
      imageUrl: resolveImageUrl(item.imageUrl) || FALLBACK_IMAGE,
    })) as unknown as ServiceContent[]
  }, [language, t])
}
